import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PlayerRace } from '@kaeliux/shared';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { useAuthStore } from '../store/authStore';
import { cn } from '../utils/cn';
import { RACE_PORTRAITS, raceLabel } from '../utils/raceAssets';
import { RACE_ACCENT_VAR, RACE_BG_TINT } from '../utils/raceColors';
import { FACTION_COPY, FACTION_ORDER } from '../utils/factionCopy';

/**
 * One-time faction picker shown after universe selection. Cards are compact
 * portrait-left rows on phones so all three and the confirm button share one
 * screen, and become portrait-topped columns from `md` up.
 */
export default function RaceSelection() {
    const navigate = useNavigate();
    const { profile, setRace } = useAuthStore();
    const [selectedRace, setSelectedRace] = useState<PlayerRace | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const handleConfirm = async () => {
        if (!selectedRace || isSubmitting) return;

        setIsSubmitting(true);
        setSubmitError(null);
        try {
            await setRace(selectedRace);
            navigate('/planet', { replace: true });
        } catch (error) {
            console.error('Failed to set race:', error);
            setSubmitError(error instanceof Error ? error.message : 'Your planet could not be created. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (profile?.race) {
        return null;
    }

    return (
        <div className="relative flex min-h-[100dvh] flex-col px-4 pt-6 sm:px-6 md:justify-center md:py-10">
            <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-5 md:flex-none md:gap-8">
                <header className="animate-rise-in flex flex-col gap-1.5">
                    <h1 className="text-display text-content-primary">Choose Your Faction</h1>
                    <p className="max-w-[52ch] text-body text-content-secondary">
                        Each race plays differently. Pick the one you&rsquo;ll lead. This choice is permanent.
                    </p>
                </header>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
                    {FACTION_ORDER.map((race, index) => {
                        const copy = FACTION_COPY[race];
                        const isSelected = selectedRace === race;
                        return (
                            <button
                                key={race}
                                type="button"
                                data-testid={`faction-card-${race}`}
                                onClick={() => setSelectedRace(race)}
                                aria-pressed={isSelected}
                                disabled={isSubmitting}
                                className={cn(
                                    'animate-rise-in group relative grid grid-cols-[84px_1fr] items-center gap-3 rounded-tile border bg-surface-panel p-3 text-left',
                                    'transition-[border-color,background-color,transform] duration-200 active:scale-[0.99]',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hairline-strong focus-visible:ring-offset-2 focus-visible:ring-offset-space-900',
                                    'disabled:cursor-not-allowed disabled:opacity-50',
                                    'md:flex md:flex-col md:items-stretch md:gap-4 md:p-4',
                                    isSelected ? 'border-2 shadow-e3' : 'border-hairline hover:border-hairline-strong',
                                )}
                                style={{
                                    animationDelay: `${120 + index * 60}ms`,
                                    ...(isSelected && {
                                        borderColor: RACE_ACCENT_VAR[race],
                                        backgroundImage: `linear-gradient(${RACE_BG_TINT[race]}, ${RACE_BG_TINT[race]})`,
                                    }),
                                }}
                            >
                                <span className="relative flex h-24 items-end justify-center overflow-hidden rounded-chip bg-surface-chip md:h-48 lg:h-56">
                                    <span
                                        aria-hidden="true"
                                        className={cn(
                                            'absolute inset-0 transition-opacity duration-300',
                                            isSelected ? 'opacity-80' : 'opacity-40 group-hover:opacity-60',
                                        )}
                                        style={{
                                            background: `radial-gradient(70% 70% at 50% 100%, ${RACE_ACCENT_VAR[race]}, transparent 75%)`,
                                        }}
                                    />
                                    <img
                                        src={RACE_PORTRAITS[race]}
                                        alt=""
                                        decoding="async"
                                        className="relative h-full w-auto object-contain"
                                    />
                                </span>

                                <span className="flex min-w-0 flex-col gap-1.5 pr-6 md:gap-2 md:pr-0">
                                    <span className="flex flex-wrap items-baseline gap-x-2">
                                        <span className="text-heading text-content-primary">{raceLabel(race)}</span>
                                        <span className="text-label text-content-muted">{copy.epithet}</span>
                                    </span>
                                    <span className="text-body text-content-secondary">{copy.pitch}</span>
                                    <span className="flex flex-wrap gap-x-3 gap-y-1">
                                        {copy.perks.map((perk) => (
                                            <span
                                                key={perk.text}
                                                className="inline-flex items-center gap-1 text-label text-content-primary"
                                            >
                                                <Icon
                                                    name={perk.kind === 'edge' ? 'plus' : 'minus'}
                                                    size={14}
                                                    className={perk.kind === 'edge' ? 'text-status-success' : 'text-status-warning'}
                                                />
                                                {perk.text}
                                            </span>
                                        ))}
                                    </span>
                                </span>

                                {isSelected && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-space-950"
                                        style={{ backgroundColor: RACE_ACCENT_VAR[race] }}
                                    >
                                        <Icon name="check" size={14} strokeWidth={2.6} />
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="sticky bottom-0 z-10 -mx-4 mt-auto flex flex-col gap-2 bg-gradient-to-t from-surface-void via-surface-void/95 to-transparent px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:-mx-6 sm:px-6 md:static md:mx-0 md:mt-0 md:flex-row md:items-center md:justify-end md:bg-none md:p-0">
                    {submitError && (
                        <p className="text-body text-status-danger md:mr-auto" role="alert">
                            {submitError}
                        </p>
                    )}
                    <Button
                        data-testid="confirm-faction"
                        onClick={handleConfirm}
                        disabled={!selectedRace || isSubmitting}
                        loading={isSubmitting}
                        variant="primary"
                        size="lg"
                        fullWidth
                        className="md:w-auto md:min-w-[220px]"
                    >
                        {selectedRace ? `Lead the ${raceLabel(selectedRace)}` : 'Choose a faction'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
